import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useModulePrices, ModulePriceFormData } from "@/hooks/useModulePrices";
import { ModulePrice } from "@/types/marketplace";

interface EditModuleDialogProps {
  module: ModulePrice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditModuleDialog = ({ module, open, onOpenChange }: EditModuleDialogProps) => {
  const { updateModule, isUpdating } = useModulePrices();

  const [formData, setFormData] = useState<ModulePriceFormData>({
    code: '', name: '', description: '', price: 0, is_active: true, icon: null, display_order: null,
  });

  useEffect(() => {
    if (module) {
      setFormData({
        code: module.code,
        name: module.name,
        description: module.description,
        price: module.price,
        is_active: module.is_active,
        icon: module.icon,
        display_order: module.display_order,
      });
    }
  }, [module]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (module) {
      updateModule({ id: module.id, data: formData });
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar Módulo</DialogTitle>
          <DialogDescription>Edite as informações do módulo</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="code">Código *</Label>
              <Input id="code" value={formData.code} onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))} required />
            </div>
            <div>
              <Label htmlFor="name">Nome *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required />
            </div>
          </div>
          <div>
            <Label htmlFor="description">Descrição</Label>
            <Textarea id="description" value={formData.description || ''} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price">Preço Mensal (R$) *</Label>
              <Input id="price" type="number" min="0" step="0.01" value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} required />
            </div>
            <div>
              <Label htmlFor="display_order">Ordem de Exibição</Label>
              <Input id="display_order" type="number" value={formData.display_order || ''} onChange={(e) => setFormData(prev => ({ ...prev, display_order: e.target.value ? parseInt(e.target.value) : null }))} />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Switch id="is_active" checked={formData.is_active} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))} />
            <Label htmlFor="is_active">Módulo Ativo</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isUpdating}>Salvar Alterações</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
